import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import GlobalFooter from '../components/GlobalFooter';
import SeoHead from '../components/SeoHead';
import { brandColors, lightTheme } from '../styles/theme';
import TermsContent from '../components/legal/TermsContent';

import EffectiveDate from '../components/legal/EffectiveDate';
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Header for navigation
const HomeHeader = styled.header`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
  background: rgba(250, 250, 248, 0.92);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: ${lightTheme.shadows.md};
  border: 1px solid ${brandColors.border};

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderIconLink = styled(Link)`
  color: ${brandColors.textSecondary};
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;

  &:hover {
    color: ${brandColors.textPrimary};
    background-color: ${lightTheme.colors.backgroundHover};
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }

  &.active {
    background-color: ${lightTheme.colors.backgroundActive};
    color: ${brandColors.primaryBlack};

    svg {
      opacity: 1;
    }
  }
`;

const HeaderIconButton = styled.button`
  color: ${brandColors.textSecondary};
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;

  &:hover {
    color: ${brandColors.textPrimary};
    background-color: ${lightTheme.colors.backgroundHover};
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 44px;
  height: 44px;
  border: 1px solid ${brandColors.border};
  border-radius: 10px;
  background: rgba(250, 250, 248, 0.95);
  align-items: center;
  justify-content: center;
  z-index: 1100;
  cursor: pointer;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const MobileMenuDropdown = styled.div<{ $open: boolean }>`
  display: none;
  position: fixed;
  top: 4.25rem;
  right: 1rem;
  width: min(260px, calc(100vw - 1.5rem));
  background: #fff;
  border: 1px solid ${brandColors.border};
  border-radius: 12px;
  box-shadow: ${lightTheme.shadows.lg};
  z-index: 1200;
  padding: 0.5rem 0;
  flex-direction: column;
  opacity: ${props => props.$open ? 1 : 0};
  transform: ${props => props.$open ? 'translateY(0)' : 'translateY(-8px)'};
  pointer-events: ${props => props.$open ? 'auto' : 'none'};
  transition: opacity 0.2s ease, transform 0.2s ease;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuLink = styled(Link)`
  color: ${brandColors.textSecondary};
  text-decoration: none;
  min-height: 44px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;

  &:hover {
    background: ${lightTheme.colors.backgroundHover};
    color: ${brandColors.textPrimary};
  }
`;

const MobileMenuButton = styled.button`
  color: ${brandColors.textSecondary};
  background: transparent;
  border: none;
  width: 100%;
  min-height: 44px;
  padding: 0.75rem 1rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${lightTheme.colors.backgroundHover};
    color: ${brandColors.textPrimary};
  }
`;

const PageContainer = styled.div`
  padding: 6rem 2.5rem 0;
  max-width: 1200px;
  margin: 0 auto;
  font-family: ${lightTheme.fonts.text};
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 0;
  }
`;

const TermsHeader = styled.div`
  background: linear-gradient(135deg, ${brandColors.backgroundSoftWhite} 0%, #F0EDE6 100%);
  color: ${brandColors.textPrimary};
  padding: 3rem 4rem;
  border-radius: 20px;
  margin-bottom: 4rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(198, 167, 94, 0.08);
  border-bottom: 3px solid ${brandColors.accentGold};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const TermsTitle = styled.h1`
  font-family: ${lightTheme.fonts.display};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  position: relative;
  z-index: 2;
  letter-spacing: -0.02em;
  color: ${brandColors.primaryBlack};

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    padding: 0;
  }
`;


const FooterWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;

const Terms: React.FC = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  
  useEffect(() => {
    window.scrollTo(0, 0);

    const timeoutIds = [
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 100),
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 300),
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 500)
    ];

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, []);
  
  const scrollToLogin = () => {
    // Navigate to home page and scroll to the login section
    navigate('/');
    setTimeout(() => {
      const loginSection = document.getElementById('login-section');
      loginSection?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!menuOpen) return;
      const target = event.target as Node;
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  return (
    <PageContainer>
      <SeoHead
        title="Terms of Service | prest0"
        tabTitle="terms of service"
        description="Review prest0's Terms of Service governing use of our enterprise legal AI platform for law firms and legal professionals."
        keywords="terms of service, prest0 terms, enterprise legal AI terms, legal AI platform agreement"
        canonicalUrl="https://prest0.ai/terms-of-service"
        schemaType="WebPage"
      />
      <HomeHeader>
        
        <HeaderIconLink to="/team">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </HeaderIconLink>
        <HeaderIconLink to="/mission">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
            <line x1="4" y1="22" x2="4" y2="15"></line>
          </svg>
          mission
        </HeaderIconLink>
        <HeaderIconLink to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          home
        </HeaderIconLink>
        <HeaderIconButton onClick={scrollToLogin}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
            <polyline points="10 17 15 12 10 7"/>
            <line x1="15" y1="12" x2="3" y2="12"/>
          </svg>
          login
        </HeaderIconButton>
      </HomeHeader>

      <MobileMenuToggle aria-label="Toggle menu" onClick={() => setMenuOpen((prev) => !prev)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {menuOpen ? (
            <path d="M18 6L6 18M6 6l12 12" />
          ) : (
            <>
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
      </MobileMenuToggle>

      <MobileMenuDropdown $open={menuOpen} ref={mobileMenuRef}>
        <MobileMenuLink to="/team" onClick={() => setMenuOpen(false)}>team</MobileMenuLink>
        <MobileMenuLink to="/mission" onClick={() => setMenuOpen(false)}>mission</MobileMenuLink>
        <MobileMenuLink to="/" onClick={() => setMenuOpen(false)}>home</MobileMenuLink>
        <MobileMenuButton onClick={() => { setMenuOpen(false); scrollToLogin(); }}>login</MobileMenuButton>
      </MobileMenuDropdown>
      
      <TermsHeader>
        <TermsTitle>terms of service</TermsTitle>
        <EffectiveDate document="terms" />
      </TermsHeader>
      
      <ContentContainer>
        <TermsContent />
      </ContentContainer>
      
      <FooterWrapper>
        <GlobalFooter />
      </FooterWrapper>
    </PageContainer>
  );
};

export default Terms; 
